import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import {
  Box,
  Button,
  Heading,
  Text,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  useContext,
} from 'react';

import {
  ALL_PRODUCTS_LABEL_ID,
  BI,
} from '../../../constants';
import { Context } from '../../../context';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import { isMobile, isTablet } from '../../../utils/user-agent';
import { useStore } from '../../../zustand/stores';
import { pushUrl } from '../../InnerLink';

import css from './index.module.scss';
const RoadmapFeatureRequest = dynamic(
  () => import('../../RoadmapFeatureRequest')
);
type RoadmapHeaderProps = {
  roadmapLabels?: {
    id: string;
    name: string;
  }[];
  selectedLabelId?: string;
  isScrolled?: boolean;
};

export const RoadmapHeader: FunctionComponent<RoadmapHeaderProps> = ({
  selectedLabelId,
  roadmapLabels,
  isScrolled,
}) => {
  const { t } = useTranslation();
  const context = useContext(Context);
  const { query } = useRouter();
  const { locale } = useRouter();
  const { isWixStudioUser } = useStore((state) => state.userSlice);
  const isMobileView = isMobile(context);
  const isTabletView = isTablet(context);
  const { sendBIEvent } = useBI();
  const { showRequestFeature } = query;

  const midOrNormalClassName = `${css.backgroundWrapper} ${isTabletView ? css.midSize : ''
    }`;


  const setShowFeatureRequestModal = async (show: boolean) => {
    if (isWixStudioUser) {
      const newUrl = new URL(window.location.href);
      if (show) {
        newUrl.searchParams.set('showRequestFeature', 'true');
      } else {
        newUrl.searchParams.delete('showRequestFeature');
        newUrl.searchParams.delete('origin');
      }
      await pushUrl(`${newUrl.pathname}${newUrl.search}`, true);
    }
  };

  const onClickFeatureRequest = async () => {
    await setShowFeatureRequestModal(true);
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: 'request_a_feature',
        clicked_text: 'request_a_feature',
        kb_lang: locale as string,
        item_id:
          selectedLabelId && selectedLabelId !== ALL_PRODUCTS_LABEL_ID
            ? selectedLabelId
            : undefined,
      })
    );
  };

  const className = isMobileView
    ? css.backgroundWrapperMobile
    : midOrNormalClassName;

  return (
    <>
      <Box
        className={`${className} ${css.roadmapV2} ${isScrolled ? css.scrolled : ''
          }`}
      >
        {isMobileView ? (
          <Box
            dataHook={DATA_HOOKS.ROADMAP_HEADER}
            verticalAlign="middle"
            className={css.roadmapMobileHeader}
          >
            <Text
              weight="bold"
              light={isMobileView}
              className={css.roadmapMobileHeaderTitle}
            >
              {t('roadmap.breadcrumbs')}
            </Text>
          </Box>
        ) : (
          <Box
            dataHook={DATA_HOOKS.ROADMAP_HEADER}
            verticalAlign="middle"
            direction="vertical"
            align="left"
            className={css.roadmapHeader}
          >
            <Heading className={css.roadMapHeading} appearance="H3">
              {t('roadmap.page.heading')}
            </Heading>
            {!isTabletView && !isScrolled && (
              <Text
                dataHook={DATA_HOOKS.ROADMAP_SUBHEADER}
                className={css.roadMapSubHeading}
              >
                {t('roadmap.page.sub-heading')}
              </Text>
            )}
          </Box>
        )}
        {isWixStudioUser && !isMobileView && (
          <Button
            className={css.requestFeatureButton}
            onClick={onClickFeatureRequest}
            priority="secondary"
            skin="dark"
            size={'small'}
          >
            {t('roadmap.feature-request')}
          </Button>
        )}
      </Box>
      {isWixStudioUser && !!showRequestFeature && roadmapLabels && (
        <RoadmapFeatureRequest
          labels={roadmapLabels}
          isOpen={!!showRequestFeature}
          onClose={() => setShowFeatureRequestModal(false)}
        />
      )}
    </>
  );
};
